/** Appearance is a device preference, shared by profiles using this browser. */
export const THEME_STORAGE_KEY = 'streamit.theme.v1';
export const THEME_PRESETS = [
  {
    id: 'streamit-dark',
    name: 'Streamit Dark',
    description: 'The original sea, coral and cream.',
    colors: ['#060B0C', '#F3E8D6', '#FF5C47', '#3FA79F'],
  },
  {
    id: 'cinema-red',
    name: 'Cinema Red',
    description: 'Netflix-inspired red with true black.',
    colors: ['#090909', '#F5F5F5', '#FF4D5E', '#FF8D98'],
  },
  {
    id: 'midnight-blue',
    name: 'Midnight Blue',
    description: 'Deep navy with clear blue accents.',
    colors: ['#07111F', '#EBF2FF', '#76B5FF', '#7CC4DA'],
  },
  {
    id: 'forest',
    name: 'Forest',
    description: 'Evergreen with soft sage highlights.',
    colors: ['#08130F', '#EDF4E8', '#A5D59A', '#73BAA1'],
  },
  {
    id: 'amethyst',
    name: 'Amethyst',
    description: 'Plum and violet for evening viewing.',
    colors: ['#120D1B', '#F2ECFC', '#C4A3FF', '#B6ACE2'],
  },
  {
    id: 'amber',
    name: 'Amber',
    description: 'Warm charcoal with golden accents.',
    colors: ['#171108', '#FBF0DB', '#F2BD63', '#D8B77C'],
  },
  {
    id: 'slate-light',
    name: 'Slate Light',
    description: 'A bright canvas with cool slate text.',
    colors: ['#F2F5F8', '#182536', '#A22C3D', '#166A70'],
  },
] as const;
export type ThemeId = (typeof THEME_PRESETS)[number]['id'];
export const DEFAULT_THEME: ThemeId = 'streamit-dark';
export function themeId(value: unknown): ThemeId {
  return THEME_PRESETS.some((theme) => theme.id === value)
    ? (value as ThemeId)
    : DEFAULT_THEME;
}
type PreferenceStorage = Pick<Storage, 'getItem' | 'setItem'>;
export function readStoredTheme(storage?: PreferenceStorage): ThemeId {
  try {
    const value = JSON.parse(
      (storage ?? localStorage).getItem(THEME_STORAGE_KEY) || 'null',
    );
    return value?.version === 1 ? themeId(value.theme) : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}
export function persistTheme(
  value: unknown,
  storage?: PreferenceStorage,
): boolean {
  try {
    const target = storage ?? localStorage;
    const encoded = JSON.stringify({ version: 1, theme: themeId(value) });
    target.setItem(THEME_STORAGE_KEY, encoded);
    return target.getItem(THEME_STORAGE_KEY) === encoded;
  } catch {
    return false;
  }
}

let current: { theme: ThemeId; error: string } = {
  theme: DEFAULT_THEME,
  error: '',
};
const listeners = new Set<() => void>();
let dispose: (() => void) | undefined;
export const readThemePreferences = () => current;
export const subscribeThemePreferences = (notify: () => void) => {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
};
function apply(theme: ThemeId, error = '') {
  document.documentElement.dataset.streamitTheme = theme;
  document.documentElement.style.colorScheme =
    theme === 'slate-light' ? 'light' : 'dark';
  document.documentElement.classList.toggle('dark', theme !== 'slate-light');
  if (current.theme === theme && current.error === error) return;
  current = { theme, error };
  listeners.forEach((notify) => notify());
}
export function startThemePreferences() {
  if (dispose || typeof window === 'undefined') return;
  const storage = (event: StorageEvent) => {
    if (event.key === THEME_STORAGE_KEY || event.key === null)
      apply(readStoredTheme());
  };
  window.addEventListener('storage', storage);
  dispose = () => {
    window.removeEventListener('storage', storage);
    dispose = undefined;
  };
  apply(readStoredTheme());
}
export function saveTheme(value: unknown) {
  startThemePreferences();
  const theme = themeId(value);
  const saved = persistTheme(theme);
  apply(
    theme,
    saved
      ? ''
      : 'Could not save on this device. This theme lasts until you reload or close this page.',
  );
}
if (import.meta.hot) import.meta.hot.dispose(() => dispose?.());
